import { useCallback } from 'react';
import { DEFAULT_STATUSES } from '../../FlowchartView/TaskNode/constants';
import { getFallbackStatus } from '../../FlowchartView/utils/statusUtils';

/**
 * useStatusHandlers Hook
 * 
 * Handles status CRUD operations (add, delete, rename, WIP limit).
 */
export const useStatusHandlers = ({ setLocalSprint }) => {
  // Handle adding a new status
  const handleAddStatus = useCallback((newStatus) => {
    console.log('➕ Adding new status:', newStatus);
    setLocalSprint(prev => {
      if (!prev) return prev;
      const currentStatuses = prev.statuses || DEFAULT_STATUSES;
      const updatedStatuses = [...currentStatuses, newStatus];
      console.log('📝 Updated statuses array:', updatedStatuses);
      return {
        ...prev,
        statuses: updatedStatuses,
      };
    });
  }, [setLocalSprint]);
  
  // Handle deleting a status
  const handleDeleteStatus = useCallback((statusId) => {
    console.log('🗑️ Deleting status:', statusId);
    setLocalSprint(prev => {
      if (!prev) return prev;
      const currentStatuses = prev.statuses || DEFAULT_STATUSES;
      
      // Get fallback status for tasks that have this status
      const fallbackStatusId = getFallbackStatus(statusId, currentStatuses);
      
      // Remove the status
      const updatedStatuses = currentStatuses.filter(s => s.id !== statusId);
      
      // Update any tasks that had this status
      const updatedTasks = (prev.tasks || []).map(task => {
        if (task.status === statusId) {
          return { ...task, status: fallbackStatusId };
        }
        return task;
      });
      
      console.log('📝 Updated statuses array:', updatedStatuses);
      return {
        ...prev,
        statuses: updatedStatuses,
        tasks: updatedTasks,
      };
    });
  }, [setLocalSprint]);
  
  // Handle renaming a status
  const handleRenameStatus = useCallback((statusId, newName) => {
    if (!statusId || !newName?.trim()) return;
    
    setLocalSprint(prev => {
      if (!prev) return prev;
      const currentStatuses = prev.statuses || DEFAULT_STATUSES;
      
      const updatedStatuses = currentStatuses.map(s => 
        s.id === statusId ? { ...s, name: newName.trim() } : s
      );
      
      return {
        ...prev,
        statuses: updatedStatuses,
      };
    });
  }, [setLocalSprint]);
  
  // Handle setting WIP limit on a status
  const handleSetWipLimit = useCallback((statusId, limit) => {
    setLocalSprint(prev => {
      if (!prev) return prev;
      const currentStatuses = prev.statuses || DEFAULT_STATUSES;
      
      const updatedStatuses = currentStatuses.map(s => 
        s.id === statusId ? { ...s, wipLimit: limit } : s
      );
      
      return {
        ...prev,
        statuses: updatedStatuses,
      };
    });
  }, [setLocalSprint]);

  return {
    handleAddStatus,
    handleDeleteStatus,
    handleRenameStatus,
    handleSetWipLimit,
  };
};

export default useStatusHandlers;
